// [AI_START::module id="feature-flags/fallback.ts" purpose="Lambda HTTP evaluate when gate GraphQL is unavailable" links=["useEvaluateFeatureFlags", "feature-flags/types.ts"]]
import { FeatureFlagResult, FeatureFlagTrait } from "~@/feature-flags/types";

/** Separate budgets so a slow Auth0 token call does not starve the Lambda fetch. */
const FALLBACK_TOKEN_TIMEOUT_MS = 8_000;
const FALLBACK_FETCH_TIMEOUT_MS = 8_000;

// [AI_START::function id="traitsToQueryParams" purpose="Serialize optional traits into Lambda evaluate query string" links=["fetchFeatureFlagsFallback"]]
function traitsToQueryParams(traits?: FeatureFlagTrait[]): string {
  if (!traits?.length) {
    return "";
  }
  return traits
    .filter((trait) => trait.key && trait.value != null && trait.value !== "")
    .map(
      (trait) =>
        `trait.${encodeURIComponent(trait.key)}=${encodeURIComponent(
          String(trait.value)
        )}`
    )
    .join("&");
}
// [AI_END::traitsToQueryParams]

// [AI_START::function id="failClosedFlags" purpose="Disabled stub evaluations when fallback cannot run" links=["fetchFeatureFlagsFallback"]]
function failClosedFlags(flagIds: string[]): FeatureFlagResult[] {
  return flagIds.map((flagId) => ({
    flagId,
    enabled: false,
    value: null,
  }));
}
// [AI_END::failClosedFlags]

// [AI_START::function id="withTimeout" purpose="Reject a promise after ms so hung Auth0/fetch cannot block the UI" links=["fetchFeatureFlagsFallback"]]
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timerId = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timerId);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timerId);
        reject(err);
      }
    );
  });
}
// [AI_END::withTimeout]

// [AI_START::function id="fetchFeatureFlagsFallback" purpose="GET /flags/evaluate via REACT_APP_FLAGS_FALLBACK_URL with per-step timeouts; fail closed if unset" links=["useEvaluateFeatureFlags", "traitsToQueryParams", "withTimeout"]]
export async function fetchFeatureFlagsFallback(
  flagIds: string[],
  getAccessToken: () => Promise<string>,
  traits?: FeatureFlagTrait[]
): Promise<FeatureFlagResult[]> {
  const base = process.env.REACT_APP_FLAGS_FALLBACK_URL;
  if (!base) {
    return failClosedFlags(flagIds);
  }

  const token = await withTimeout(
    getAccessToken(),
    FALLBACK_TOKEN_TIMEOUT_MS,
    "Feature flags fallback token"
  );

  const controller = new AbortController();
  const fetchTimerId = window.setTimeout(() => {
    controller.abort();
  }, FALLBACK_FETCH_TIMEOUT_MS);

  try {
    const flagIdsParam = encodeURIComponent(flagIds.join(","));
    const traitQuery = traitsToQueryParams(traits);
    const suffix = traitQuery ? `&${traitQuery}` : "";
    const res = await fetch(
      `${base.replace(/\/$/, "")}/flags/evaluate?flagIds=${flagIdsParam}${suffix}`,
      {
        headers: { authorization: `Bearer ${token}` },
        signal: controller.signal,
      }
    );
    if (!res.ok) {
      throw new Error(`Fallback HTTP ${res.status}`);
    }
    const body = (await res.json()) as {
      evaluations?: FeatureFlagResult[];
    };
    return body.evaluations ?? [];
  } finally {
    window.clearTimeout(fetchTimerId);
  }
}
// [AI_END::fetchFeatureFlagsFallback]
// [AI_END::feature-flags/fallback.ts]
